import React, { FC, useContext, useState } from 'react';
import clsx from 'clsx';
import { ExclamationInTriangle } from '@td-client-frontend/icons';
import { Toggle, ButtonWithIconAndLoader } from '@td-client-frontend/common';
import Modal from '../../Modal/NewModal';
import { Button } from '../../Button';
import WithPermission, { PermMode } from '../../WithPermission';
import { useAuth } from '../../../context/AuthContext';
import { useExсludeFromMarketing, useMarketingSearchCache } from '../../../hooks/useMarketingPreferences';
import { PendingChangesContext } from '../../../context/PendingChangesContext';
import { MarketingHierarchyRowData } from '../../../@types/Marketing';
import { Nullable } from '../../../@types/common';
import { useMarketingProfileCache } from '../../../hooks/useMarketingProfile';
import { Permissions } from '../../../@types/Permissions';

interface Props {
  groupOrgData: Nullable<MarketingHierarchyRowData>;
  isExcluded: boolean;
}

export const ExcludeFromMarketing: FC<Props> = ({ groupOrgData, isExcluded }) => {
  const { checkPerm } = useAuth();
  const { includeOrExcludeFromMarketing, isLoading } = useExсludeFromMarketing();
  const { checkAndPrompt } = useContext(PendingChangesContext);
  const { invalidateMarketingSearchCache } = useMarketingSearchCache();
  const { setProfileCache } = useMarketingProfileCache();

  const [excludeModalOpen, setExcludeModalOpen] = useState<boolean>(false);

  const toggleExcludeModalOpen = () => {
    checkAndPrompt(() => {
      if (!isLoading) setExcludeModalOpen((prev) => !prev);
    });
  };

  const toggleMarketingExclude = async () => {
    if (groupOrgData?.id) {
      await includeOrExcludeFromMarketing({ id: groupOrgData.id, exclude: !isExcluded });
      invalidateMarketingSearchCache();
      setProfileCache(groupOrgData!.assignedMarketingProfile!.id, null);
      setExcludeModalOpen(false);
    }
  };

  const CustomButtons = () => (
    <div className="flex gap-4">
      <Button title="Cancel" kind="secondary" onClick={toggleExcludeModalOpen} disabled={isLoading} />
      <ButtonWithIconAndLoader
        title="Confirm and Save"
        loading={isLoading}
        className="flex items-center justify-center font-effra btn min-w-12"
        labelClasses={clsx('text-normal', isLoading && 'hidden')}
        onClick={toggleMarketingExclude}
      />
    </div>
  );
  return (
    <div
      className={clsx('flex items-center mb-4', isExcluded ? 'justify-between' : 'justify-end')}
      data-testid="exlude-or-include-from-marketing"
    >
      {isExcluded && (
        <div
          className={clsx(
            'sm:p-2 md:p-6 bg-yellow-30 text-center flex items-center justify-center',
            !checkPerm([Permissions.MarketingCampaignExcludeWrite]) && 'w-full'
          )}
          data-testid="exlude-banner"
        >
          <ExclamationInTriangle className="mr-2 h-4 min-w-4" fill="#181919" data-testid="exluded-icon" />
          <p className="font-bold">Temporarily excluded from all marketing campaigns</p>
        </div>
      )}
      <WithPermission requiredPerms={[Permissions.MarketingCampaignExcludeWrite]} mode={PermMode.ALL}>
        <div className="flex items-center ml-2">
          <span className="mr-2">Exclude from marketing campaigns</span>
          <Toggle
            name="excludeFromMarketing"
            onChange={toggleExcludeModalOpen}
            value={isExcluded}
            disabled={!!groupOrgData?.ancestorMarketingExclusion}
          />
          <Modal
            isOpen={excludeModalOpen}
            onClose={toggleExcludeModalOpen}
            header={isExcluded ? 'Include in marketing' : 'Exclude from marketing'}
            size="small"
            customFooterActions={{
              right: <CustomButtons />
            }}
          >
            <h3 className="flex flex-row ml-5 mr-4 mt-5 mb-8" data-testid="exlude-or-include-modal">
              {isExcluded ? (
                <>
                  By making this update, marketing settings will be restored and this client and/or groups will be
                  eligible to be included in marketing activities. Are you sure you want to proceed?
                </>
              ) : (
                <>
                  <ExclamationInTriangle className="mt-1 mr-4 h-6 w-16" /> Are you sure you want to exclude this client
                  and/or groups from all marketing activities? All marketing activities will be suspended until approval
                  is restored.
                </>
              )}
            </h3>
          </Modal>
        </div>
      </WithPermission>
    </div>
  );
};
